import { createContext, useCallback, useContext, type ReactNode } from "react";
import { useApp } from "@/hooks/use-app-context";

export interface ConfirmOptions {
  title: ReactNode;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** "danger" for anything that deletes, suspends, pays or signs out. */
  tone?: "default" | "danger";
}

export type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

export const ConfirmContext = createContext<ConfirmFn | null>(null);

export function useConfirm(): ConfirmFn {
  const confirm = useContext(ConfirmContext);
  if (!confirm) throw new Error("useConfirm must be used inside <ConfirmProvider>");
  return confirm;
}

/** Wraps a logout action behind the same confirmation in every space. */
export function useConfirmedLogout(logout: () => void) {
  const confirm = useConfirm();
  const { t } = useApp();
  return useCallback(async () => {
    const ok = await confirm({
      title: t.logoutConfirmTitle,
      description: t.logoutConfirmDescription,
      confirmLabel: t.logoutConfirmAction,
      tone: "danger",
    });
    if (ok) logout();
  }, [confirm, logout, t]);
}
